import * as React from "react"
import type { Release } from "@packages/commons"
import DropdownMenu from "@imminently/decisively-ui/DropdownMenu"
import Icon from "@imminently/decisively-ui/Icon"
import {
  WorkspaceCard,
  ProjectCard,
  ReleaseCard,
  InterviewCard,
  BotCard,
  CardSkeleton,
} from "@/ui/blocks/cards"
import { ComponentPage, ComponentSection, ComponentPreview } from "../components/ComponentPage"

// ─── Shared dummy menu ────────────────────────────────────────────────────────

const DemoMenu = () => (
  <>
    <DropdownMenu.Item>
      <DropdownMenu.Item.Label>Edit</DropdownMenu.Item.Label>
      <DropdownMenu.Item.Icon><Icon name="edit" /></DropdownMenu.Item.Icon>
    </DropdownMenu.Item>
    <DropdownMenu.Item color="destructive">
      <DropdownMenu.Item.Label>Delete</DropdownMenu.Item.Label>
      <DropdownMenu.Item.Icon><Icon name="delete" /></DropdownMenu.Item.Icon>
    </DropdownMenu.Item>
  </>
)

const USERS = [
  { id: "1", name: "Alice Johnson" },
  { id: "2", name: "Bob Smith" },
  { id: "3", name: "Carol White" },
  { id: "4", name: "David Brown" },
  { id: "5", name: "Eve Davis" },
  { id: "6", name: "Frank Miller" },
]

const MOCK_RELEASES = [
  {
    id: "r1",
    model: "project-1",
    releaseNo: 3,
    description: "Stable production release",
    activeVersionFlag: true,
    lastModified: new Date(Date.now() - 1000 * 60 * 60 * 6).toISOString(),
    basedOn: 2,
    promotedAs: 1,
    status: "ready",
  },
  {
    id: "r2",
    model: "project-1",
    releaseNo: 2,
    description: "Next generation claims engine",
    activeVersionFlag: false,
    lastModified: new Date(Date.now() - 1000 * 60 * 60 * 24 * 5).toISOString(),
    basedOn: 1,
    status: "ready",
  },
  {
    id: "r3",
    model: "project-1",
    releaseNo: 1,
    description: "Initial release",
    activeVersionFlag: false,
    lastModified: new Date(Date.now() - 1000 * 60 * 60 * 24 * 30).toISOString(),
    status: "ready",
  },
] as unknown as Release[]

const noop = () => { }

// ─── Grid wrapper ─────────────────────────────────────────────────────────────

const CardGrid = ({ children }: { children: React.ReactNode }) => (
  <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
    {children}
  </div>
)

// ─── Page ─────────────────────────────────────────────────────────────────────

export function CardsBlockPage() {
  return (
    <ComponentPage
      title="Resource Cards"
      description="Purpose-built card blocks for the major resource types. Each card exposes only the props it needs, with a consistent hover-reveal menu and footer action."
    >
      {/* WorkspaceCard */}
      <ComponentSection
        title="WorkspaceCard"
        description="Shows workspace info with visibility badge, tags, member avatars, and an open action."
      >
        <CardGrid>
          <WorkspaceCard
            name="Acme Corp"
            description="Primary workspace for all Acme projects"
            visibility="public"
            tags={["primary", "production"]}
            users={USERS.slice(0, 4)}
            menu={<DemoMenu />}
            action={{ name: "Open", onClick: noop }}
          />
          <WorkspaceCard
            name="Internal R&D"
            description="Experimental models and sandbox work"
            visibility="private"
            tags={["sandbox", "internal", "experimental", "do-not-promote", "temp", "archive"]}
            users={USERS}
            menu={<DemoMenu />}
            action={{ name: "Open", onClick: noop }}
          />
          <WorkspaceCard
            name="Minimal"
            description="No tags, no users, no action"
          />
        </CardGrid>
      </ComponentSection>

      {/* ProjectCard */}
      <ComponentSection
        title="ProjectCard"
        description="Project within a workspace. Supports an inline-edit name (ReactNode) and an i18n visibility label."
      >
        <CardGrid>
          <ProjectCard
            name="Claims Assessment"
            description="Automates insurance claim decisions"
            visibility="public"
            menu={<DemoMenu />}
            action={{ name: "View", onClick: noop }}
          />
          <ProjectCard
            name="Onboarding Engine"
            description="Customer eligibility & compliance checks"
            visibility="private"
            menu={<DemoMenu />}
            action={{ name: "View", onClick: noop }}
          />
          <ProjectCard
            name="Lending Rules"
            menu={<DemoMenu />}
          />
        </CardGrid>
      </ComponentSection>

      {/* ReleaseCard */}
      <ComponentSection
        title="ReleaseCard"
        description="Release entry with Current chip, last modified date, based-on provenance, and a self-managed dropdown menu."
      >
        <CardGrid>
          {MOCK_RELEASES.map((r) => (
            <ReleaseCard key={r.id} release={r} onView={noop} />
          ))}
        </CardGrid>
      </ComponentSection>

      {/* InterviewCard */}
      <ComponentSection
        title="InterviewCard"
        description="Interview within a release. Status drives the chip colour: published → success, draft → subtle."
      >
        <CardGrid>
          <InterviewCard
            name="Customer Intake"
            description="Captures primary applicant details"
            status="published"
            menu={<DemoMenu />}
            action={{ name: "View", onClick: noop }}
          />
          <InterviewCard
            name="Risk Assessment"
            description="Secondary liability & risk questions"
            status="draft"
            menu={<DemoMenu />}
            action={{ name: "View", onClick: noop }}
          />
          <InterviewCard
            name="Untitled Interview"
          />
        </CardGrid>
      </ComponentSection>

      {/* BotCard */}
      <ComponentSection
        title="BotCard"
        description="Bot within a release. Three status variants: active → success, inactive → destructive, draft → subtle."
      >
        <CardGrid>
          <BotCard
            name="Claims Bot"
            description="Handles end-to-end claims triage"
            status="active"
            menu={<DemoMenu />}
            action={{ name: "View", onClick: noop }}
          />
          <BotCard
            name="Onboarding Assistant"
            description="Guides new customers through eligibility"
            status="inactive"
            menu={<DemoMenu />}
            action={{ name: "View", onClick: noop }}
          />
          <BotCard
            name="Experimental Bot"
            description="WIP — not yet deployed"
            status="draft"
            menu={<DemoMenu />}
          />
        </CardGrid>
      </ComponentSection>

      {/* CardSkeleton */}
      <ComponentSection
        title="CardSkeleton"
        description="Loading placeholder used while card data is being fetched."
      >
        <ComponentPreview className="items-start justify-start">
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3 w-full">
            <CardSkeleton />
            <CardSkeleton />
            <CardSkeleton />
          </div>
        </ComponentPreview>
      </ComponentSection>
    </ComponentPage>
  )
}
